<template>
    <div class="resource-center-rule">
        <pendo-multiselect
            v-if="resourceCenterOptions.length != 1"
            :value="selectedResourceCenter"
            full-width
            :append-to-body="false"
            :disabled="readOnly"
            :loading="loadingResources"
            hide-selected-options
            :allow-empty="false"
            :options="resourceCenterOptions"
            :min-menu-width="250"
            value-key="resourceCenterId"
            :placeholder="$t('resourceCenterRule.Select Resource Center')"
            @select="onResourceCenterSelect">
            <template #selectedLabel="{ option }">
                <span>
                    {{ option.label }}
                </span>
                <span class="segment-chooser-option__aside">
                    {{ option.aside }}
                </span>
            </template>
            <template #option="{ option, index }">
                <pendo-actions-option
                    :option="option"
                    :index="index"
                    :actions="false" />
            </template>
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </pendo-multiselect>
        <template v-if="selectedResourceCenter">
            <pendo-multiselect
                v-if="moduleOptions.length > 1"
                :value="selectedModule"
                full-width
                :append-to-body="false"
                :disabled="readOnly"
                :allow-empty="false"
                :options="moduleOptions"
                label-key="name"
                value-key="guideId"
                :placeholder="$t('resourceCenterRule.Select Module')"
                @select="onModuleSelect" />
            <template v-if="selectedModule">
                <template v-if="!isLocaleEnglish">
                    <div class="flex-break" />
                    <strong>{{ $t('Condition') }}</strong>
                    <div class="flex-break" />
                </template>
                <fact-select
                    :options="factOptions"
                    :value="factValue"
                    :read-only="readOnly"
                    :check-valid-rule="checkValidRule"
                    @input="$emit('input', $event)" />
                <template v-if="!isLocaleEnglish">
                    <div class="flex-break" />
                    <strong>{{ $t('Criteria') }}</strong>
                    <div class="flex-break" />
                </template>
                <simple-select
                    :options="timeOptions"
                    :value="timeValue"
                    :check-valid-rule="checkValidRule"
                    preselect-first
                    field="time"
                    disabled
                    @input="$emit('input', $event)" />
            </template>
        </template>
    </div>
</template>

<script>
import get from 'lodash/get';
import { mapGetters } from 'vuex';
import { PendoActionsOption, PendoMultiselect } from '@pendo/components';
import FactSelect from '@/segments/components/segment-editor-vue/components/fact-select.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import {
    getTimeOptions,
    getFactValue,
    getFactOptions,
    formatFactOptions,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';
import { getNoResourcesFoundText } from '@/segments/components/segment-editor-vue/segment-editor.utils';

export default {
    name: 'ResourceCenterRule',
    components: {
        FactSelect,
        PendoMultiselect,
        PendoActionsOption,
        SimpleSelect,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    data () {
        return {
            timeOptions: [],
        };
    },
    computed: {
        ...mapGetters({
            getAppById: 'apps/getAppById',
        }),
        noDataText () {
            return getNoResourcesFoundText({
                rule: this.rule,
                isMultiApp: this.isMultiApp,
                resourceType: 'resourceCenter',
            });
        },
        resourceCenterOptions () {
            const resourceCenterOptions = this.resources
                .filter((guide) => {
                    const isResourceCenter = get(guide, 'attributes.resourceCenter.isTopLevel');
                    const isP2Guide = get(guide, 'attributes.type') === 'building-block';
                    const isDraft = get(guide, 'state') === 'draft';

                    return isResourceCenter && isP2Guide && isDraft;
                })
                .map((guide) => {
                    const appName = this.getAppById(guide.appId)?.displayName;

                    return {
                        resourceCenterId: guide.id,
                        label: guide.name,
                        aside: appName ? `(${appName})` : '',
                        tooltip: {
                            content: appName ? `${guide.name} (${appName})` : guide.name,
                        },
                    };
                });

            return resourceCenterOptions;
        },
        moduleOptions () {
            const moduleOptions = this.resources
                .filter((guide) => {
                    const resourceCenterId =
                        get(guide, 'attributes.resourceCenter.homeViewId') ||
                        get(guide, 'attributes.resourceCenter.originId');
                    const isRcModule = resourceCenterId === this.selectedResourceCenter.resourceCenterId;
                    const isP2Guide = get(guide, 'attributes.type') === 'building-block';
                    const isDraft = get(guide, 'state') === 'draft';

                    return isRcModule && isP2Guide && isDraft;
                })
                .map((guide) => {
                    const isHomeView = get(guide, 'attributes.resourceCenter.isTopLevel');

                    return {
                        guideId: guide.id, // can't be named moduleId because guidesSeenEver source only accepts 'guideId'
                        name: isHomeView ? this.$t('resourceCenterRule.Resource Center Home') : guide.name,
                    };
                });

            if (moduleOptions.length === 1) {
                this.$emit('input', { field: 'guideId', value: moduleOptions[0].guideId });
            }

            return moduleOptions;
        },
        selectedResourceCenter () {
            if (this.resourceCenterOptions.length === 1) {
                return this.resourceCenterOptions[0];
            }

            return this.resourceCenterOptions.find(
                (resourceCenter) => resourceCenter.resourceCenterId === this.rule.resourceCenterId,
            );
        },
        selectedModule () {
            return this.moduleOptions.find((mod) => mod.guideId === this.rule.guideId);
        },
        factOptions () {
            const options = getFactOptions({
                id: this.rule.id,
                onlyAllowGuideCompatibleRules: this.config.onlyAllowGuideCompatibleRules,
            });

            return formatFactOptions(options);
        },
        factValue () {
            return getFactValue(this.rule, this.factOptions);
        },
        timeValue () {
            return this.timeOptions.find((option) => option.time === this.rule.time);
        },
    },
    created () {
        this.timeOptions = getTimeOptions().guide;
    },
    methods: {
        onResourceCenterSelect (resourceCenter) {
            const field = ['resourceCenterId', 'guideId'];
            const value = { resourceCenterId: resourceCenter.resourceCenterId, guideId: null };

            this.$emit('input', { field, value });
        },
        onModuleSelect (mod) {
            const field = ['guideId'];
            const value = { guideId: mod.guideId };

            this.$emit('input', { field, value });
        },
    },
};
</script>

<style lang="less" scoped>
:deep(.pendo-multiselect-option--aside) {
    .pendo-multiselect-option__label-container {
        justify-content: space-between;
        display: grid;
        grid-auto-flow: column;

        .pendo-multiselect-option__label {
            margin-right: 8px;
        }
    }
}

.segment-chooser-option__aside {
    font-weight: 400;
    color: var(--palette-gray-70);
}

.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
